// Google Analytics 4 (gtag.js) with Consent Mode v2.
//
// Unlike the Meta/X pixels (which are fully blocked until consent),
// Google's Consent Mode lets us load gtag.js immediately with all
// consent signals defaulting to "denied". In that state gtag sends
// cookieless, anonymised pings that power Google's conversion modeling
// without storing anything on the user's device (PECR / ePrivacy safe).
//
// When the user grants consent via the cookie banner, we call
// updateGtagConsent() to flip the relevant signals to "granted".
//
// Consent signal mapping:
//   analytics consent  → analytics_storage
//   marketing consent  → ad_storage, ad_user_data, ad_personalization

import { hasSecretInPath } from './secretRoutes';
import { activeExperimentsAsUserProps } from './experiments';

const GA_ID = 'G-7H77WK58RB';
const ADS_ID = 'AW-18430742009';
const ADS_CONVERSION_LABEL = 'AW-18430742009/elxnCP_dwO4cEPmbu9RE';
const SCRIPT_ID = 'gtag-js';
const SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;

let initialized = false;
let lastPageViewUrl = null;

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
}

/**
 * Called once on app startup (before consentStore.init).
 * Sets all consent signals to "denied" by default, loads the gtag.js
 * script, and configures the GA4 property.
 */
export function initGtag() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (initialized) return;

  ensureDataLayer();

  // 1. Set consent defaults BEFORE loading the script.
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500, // ms to wait for updateGtagConsent()
  });

  configureTags();
}

function injectScript() {
  if (document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
}

// gtag.js is ~190 KiB of parse/execute; keep it off the critical path.
// Commands issued meanwhile are queued in dataLayer and replayed on load.
function scheduleAfterLoad(fn) {
  const runIdle = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fn, { timeout: 4000 });
    } else {
      setTimeout(fn, 1500);
    }
  };
  if (document.readyState === 'complete') {
    runIdle();
  } else {
    window.addEventListener('load', runIdle, { once: true });
  }
}

// Loads gtag.js and configures GA4 + Ads. Deferred while the URL carries a
// secret: the library records the current location (page_view, scroll,
// later `dr`) as soon as it is configured, regardless of send_page_view.
function configureTags() {
  if (initialized || hasSecretInPath()) return;

  scheduleAfterLoad(injectScript);

  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: true });
  window.gtag('config', ADS_ID);

  const experimentProps = activeExperimentsAsUserProps();
  if (Object.keys(experimentProps).length) {
    window.gtag('set', 'user_properties', experimentProps);
  }

  initialized = true;
  lastPageViewUrl = window.location.href;
}

/**
 * Update Google consent signals to match the user's choices.
 * Call this whenever consent state changes (accept, reject, preferences save).
 *
 * @param {object} opts
 * @param {boolean} opts.analytics  Whether analytics consent was granted.
 * @param {boolean} opts.marketing  Whether marketing consent was granted.
 */
export function updateGtagConsent({ analytics = false, marketing = false } = {}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
}

/**
 * Track an SPA page-view on route change.
 * De-duplicates consecutive calls for the same URL.
 */
export function trackGtagPageView() {
  if (typeof window === 'undefined' || !window.gtag) return;
  if (hasSecretInPath()) return;
  if (!initialized) {
    configureTags();
    return;
  }

  const pageUrl = window.location.href;
  if (pageUrl === lastPageViewUrl) return;
  lastPageViewUrl = pageUrl;

  window.gtag('config', GA_ID, {
    page_path: window.location.pathname + window.location.search,
  });

  // Google Ads page-view conversion
  window.gtag('event', 'conversion', {
    send_to: ADS_CONVERSION_LABEL,
    value: 1.0,
    currency: 'GBP',
  });
}
